/**
 * Frontier events — feed del catálogo REQ-FRT-015 con filtros por área,
 * rutina y fallos, en sustitución de la antigua vista de errores
 * (REQ-FRT-016 §2, revisión UI del 18-Oct-2026).
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import {
  Button,
  Card,
  CardContent,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { formatDateTime } from '../components/FrontierGuard';
import { frontierEventLabel } from '../lib/eventLabels';
import { useFrontierEvents, useFrontierRoutines } from '../hooks/useFrontier';
import type { FrontierEventFeedItem } from '../types';

const ENTITIES = [
  'routine',
  'trigger',
  'run',
  'capacity',
  'policy',
  'emergency',
  'notification',
] as const;

const DEFAULT_LIMIT = 50;

function localizedValue(
  t: (key: string) => string,
  prefix: string,
  value: unknown,
): string | null {
  if (typeof value !== 'string' || !value) return null;
  const key = `${prefix}.${value}`;
  const label = t(key);
  return label === key ? null : label;
}

function eventDetail(t: (key: string) => string, data: Record<string, unknown>): string {
  const parts: string[] = [];
  const wait = localizedValue(t, 'frontier.waits', data.wait_reason);
  if (wait) parts.push(wait);
  const origin = localizedValue(t, 'frontier.origins', data.origin);
  if (origin) parts.push(origin);
  const status = (value: unknown): string | null =>
    localizedValue(t, 'frontier.routines.status', value) ??
    localizedValue(t, 'frontier.runStatus', value);
  if (data.status) {
    const label = status(data.status);
    if (label) parts.push(label);
  }
  if (data.from || data.to) {
    const from = status(data.from);
    const to = status(data.to);
    if (from && to) parts.push(`${from} → ${to}`);
  }
  const reason = localizedValue(t, 'frontier.routines.pauseReasons', data.reason);
  if (reason) parts.push(reason);
  if (parts.length === 0) {
    // Unknown metadata: keep a compact, non-technical placeholder instead of
    // raw enum pairs (frontend-development-guide §8.3).
    return '—';
  }
  return parts.slice(0, 3).join(' · ');
}

export default function FrontierEventsPage() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(0);

  const entity = params.get('entity') ?? '';
  const routineId = params.get('routine_id') ?? '';
  const errorsOnly = params.get('errors_only') === '1';

  const { data: routines } = useFrontierRoutines();
  const { data, isLoading, isError } = useFrontierEvents({
    entity: entity || undefined,
    routine_id: routineId || undefined,
    errors_only: errorsOnly || undefined,
    limit: DEFAULT_LIMIT,
    offset: page * DEFAULT_LIMIT,
  });

  const update = (next: { entity?: string; routineId?: string; errorsOnly?: boolean }) => {
    const merged = new URLSearchParams(params);
    if (next.entity !== undefined) {
      if (next.entity) merged.set('entity', next.entity);
      else merged.delete('entity');
    }
    if (next.routineId !== undefined) {
      if (next.routineId) merged.set('routine_id', next.routineId);
      else merged.delete('routine_id');
    }
    if (next.errorsOnly !== undefined) {
      if (next.errorsOnly) merged.set('errors_only', '1');
      else merged.delete('errors_only');
    }
    setParams(merged, { replace: true });
    setPage(0);
  };

  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : page * DEFAULT_LIMIT + 1;
  const to = Math.min(total, (page + 1) * DEFAULT_LIMIT);
  const totalPages = Math.max(1, Math.ceil(total / DEFAULT_LIMIT));

  const entityOptions = useMemo(
    () => [
      { value: 'all', label: t('frontier.events.entity.all') },
      ...ENTITIES.map((item) => ({
        value: item,
        label: t(`frontier.events.entity.${item}`),
      })),
    ],
    [t],
  );

  const routineOptions = useMemo(
    () => [
      { value: 'all', label: t('frontier.events.all') },
      ...(routines ?? []).map((routine) => ({ value: routine.id, label: routine.name })),
    ],
    [routines, t],
  );

  return (
    <div className="p-6 space-y-4" data-testid="frontier-events">
      <PageHeader
        icon={Activity}
        title={t('frontier.events.title')}
        testId="frontier-events-header"
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex w-48 flex-col gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('frontier.events.filterEntity')}
          </span>
          <Select
            value={entity || 'all'}
            onValueChange={(value) => update({ entity: value === 'all' ? '' : value })}
            options={entityOptions}
          />
        </div>
        <div className="flex w-56 flex-col gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('frontier.events.filterRoutine')}
          </span>
          <Select
            value={routineId || 'all'}
            onValueChange={(value) => update({ routineId: value === 'all' ? '' : value })}
            options={routineOptions}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            data-testid="events-errors-only"
            checked={errorsOnly}
            onChange={(event) => update({ errorsOnly: event.target.checked })}
          />
          {t('frontier.events.errorsOnly')}
        </label>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Spinner size="sm" />
            </div>
          ) : isError ? (
            <p className="py-6 text-sm text-red-600" role="alert">
              {t('frontier.actions.failed')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('frontier.events.colTime')}</TableHead>
                  <TableHead>{t('frontier.events.colType')}</TableHead>
                  <TableHead>{t('frontier.events.colRoutine')}</TableHead>
                  <TableHead>{t('frontier.events.colRun')}</TableHead>
                  <TableHead>{t('frontier.events.colDetail')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items ?? []).length === 0 && (
                  <TableEmpty colSpan={5} title={t('frontier.events.empty')} />
                )}
                {(data?.items ?? []).map((item: FrontierEventFeedItem) => (
                  <TableRow key={item.id} data-testid={`event-row-${item.id}`}>
                    <TableCell>{formatDateTime(item.created_at)}</TableCell>
                    <TableCell>{frontierEventLabel(t, item.type)}</TableCell>
                    <TableCell>{item.routine_name ?? '—'}</TableCell>
                    <TableCell>
                      {item.run_id ? (
                        <Link
                          className="text-blue-600 hover:underline"
                          to={`/frontier/runs/${item.run_id}`}
                        >
                          {t('frontier.events.colRun')}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 dark:text-gray-400">
                      {eventDetail(t, item.data)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
        <span data-testid="events-pagination">
          {t('frontier.events.pagination', { from, to, total })}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((value) => Math.max(0, value - 1))}
          >
            {t('frontier.events.prev')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((value) => value + 1)}
          >
            {t('frontier.events.next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
